import { useId } from 'react';
import {
  getOtsFormControlClassName,
  OtsFormField,
  OtsFormFieldSize,
} from './OtsFormField';
import type { OtsComboboxOption } from './OtsCombobox';

export type OtsSelectOption = OtsComboboxOption;

export type OtsSelectProps = {
  id?: string;
  name?: string;
  label: string;
  description?: string;
  options: OtsSelectOption[];
  value?: string;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  loading?: boolean;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  size?: OtsFormFieldSize;
  className?: string;
};

/** Theme-token-based native select foundation for app, bridge, and dark-mode surfaces. */
export const OtsSelect = ({
  id: idProp,
  name,
  label,
  description,
  options,
  value,
  onChange,
  placeholder = 'Any',
  loading = false,
  error,
  disabled,
  required,
  size = 'default',
  className,
}: OtsSelectProps) => {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  return (
    <OtsFormField
      htmlFor={id}
      label={label}
      description={description}
      error={error}
      required={required}
      size={size}
      className={className}
    >
      <select
        id={id}
        name={name}
        required={required}
        disabled={disabled || loading}
        className={getOtsFormControlClassName(size)}
        value={value ?? ''}
        onChange={(event) => onChange(event.currentTarget.value || undefined)}
      >
        <option value="">{loading ? 'Loading options…' : placeholder}</option>
        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </option>
        ))}
      </select>
    </OtsFormField>
  );
};
